'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = TableActionMenuPlugin;
var _reactDom = require("react-dom");
var _react = _interopRequireWildcard(require("react"));
var _lexical = require("lexical");
var _LexicalComposerContext = require("@lexical/react/LexicalComposerContext");
var _table = require("@lexical/table");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function TableActionMenu(_ref) {
  let {
    onClose,
    tableCellNode,
    setIsMenuOpen,
    contextRef
  } = _ref;
  const [editor] = (0, _LexicalComposerContext.useLexicalComposerContext)();
  const dropDownRef = (0, _react.useRef)();
  const [cell_node, updateTableCellNode] = (0, _react.useState)(tableCellNode);
  const [selectionCounts, updateSelectionCounts] = (0, _react.useState)({
    columns: 1,
    rows: 1
  });
  (0, _react.useEffect)(() => {
    return editor.registerMutationListener(_table.TableCellNode, nodeMutations => {
      const nodeUpdated = nodeMutations.get(cell_node.getKey()) === 'updated';
      if (nodeUpdated) {
        editor.getEditorState().read(() => {
          updateTableCellNode(cell_node.getLatest());
        });
      }
    });
  }, [editor, cell_node]);
  (0, _react.useEffect)(() => {
    editor.getEditorState().read(() => {
      const selection = (0, _lexical.$getSelection)();
      if ((0, _lexical.$isGridSelection)(selection)) {
        const selectionShape = selection.getShape();
        updateSelectionCounts({
          columns: selectionShape.toX - selectionShape.fromX + 1,
          rows: selectionShape.toY - selectionShape.fromY + 1
        });
      }
    });
  }, [editor]);
  (0, _react.useEffect)(() => {
    const menuButtonElement = contextRef.current;
    const dropDownElement = dropDownRef.current;

    // dropdown positioning
    if (menuButtonElement != null && dropDownElement != null) {
      const dist_from_btn = 5; // tooltip minimum distance from toggle btn

      const btn_rect = menuButtonElement.getBoundingClientRect();
      const dropdown_rect = dropDownElement.getBoundingClientRect();
      let left_pos = btn_rect.right + window.pageXOffset + dist_from_btn;
      let right_pos = window.innerWidth - left_pos - dropdown_rect.width;
      if (right_pos < 5 && right_pos > 0) {
        left_pos -= right_pos;
      } else if (right_pos < 0) {
        left_pos += right_pos;
        left_pos -= 5;
      }
      dropDownElement.style.left = left_pos + 'px';
      dropDownElement.style.top = btn_rect.top + window.pageYOffset + 'px';
      dropDownElement.style.opacity = '1';
    }
  }, [contextRef, dropDownRef]);
  (0, _react.useEffect)(() => {
    function handleClickOutside(event) {
      if (dropDownRef.current != null && contextRef.current != null && !dropDownRef.current.contains(event.target) && !contextRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [setIsMenuOpen, contextRef]);
  const clearTableSelection = (0, _react.useCallback)(() => {
    editor.update(() => {
      if (cell_node.isAttached()) {
        const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
        const tableElement = editor.getElementByKey(tableNode.getKey());
        if (!tableElement) {
          throw new Error('Expected to find tableElement in DOM');
        }
        const tableSelection = (0, _table.getTableSelectionFromTableElement)(tableElement);
        tableSelection.clearHighlight();
        tableNode.markDirty();
        updateTableCellNode(cell_node.getLatest());
      }
      (0, _lexical.$setSelection)(null);
    });
  }, [editor, cell_node]);
  const insertTableRowAtSelection = (0, _react.useCallback)(shouldInsertAfter => {
    editor.update(() => {
      const selection = (0, _lexical.$getSelection)();
      const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
      let tableRowIndex;
      if ((0, _lexical.$isGridSelection)(selection)) {
        const selectionShape = selection.getShape();
        tableRowIndex = shouldInsertAfter ? selectionShape.toY : selectionShape.fromY;
      } else {
        tableRowIndex = (0, _table.$getTableRowIndexFromTableCellNode)(cell_node);
      }
      const grid = (0, _table.$getElementGridForTableNode)(editor, tableNode);
      (0, _table.$insertTableRow)(tableNode, tableRowIndex, shouldInsertAfter, selectionCounts.rows, grid);
      clearTableSelection();
      onClose();
    });
  }, [editor, cell_node, selectionCounts.rows, clearTableSelection, onClose]);
  const insertTableColumnAtSelection = (0, _react.useCallback)(shouldInsertAfter => {
    editor.update(() => {
      const selection = (0, _lexical.$getSelection)();
      const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
      let tableColumnIndex;
      if ((0, _lexical.$isGridSelection)(selection)) {
        const selectionShape = selection.getShape();
        tableColumnIndex = shouldInsertAfter ? selectionShape.toX : selectionShape.fromX;
      } else {
        tableColumnIndex = (0, _table.$getTableColumnIndexFromTableCellNode)(cell_node);
      }
      (0, _table.$insertTableColumn)(tableNode, tableColumnIndex, shouldInsertAfter, selectionCounts.columns);
      clearTableSelection();
      onClose();
    });
  }, [editor, cell_node, selectionCounts.columns, clearTableSelection, onClose]);
  const deleteTableRowAtSelection = (0, _react.useCallback)(() => {
    editor.update(() => {
      const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
      const tableRowIndex = (0, _table.$getTableRowIndexFromTableCellNode)(cell_node);
      (0, _table.$removeTableRowAtIndex)(tableNode, tableRowIndex);
      clearTableSelection();
      onClose();
    });
  }, [editor, cell_node, clearTableSelection, onClose]);
  const deleteTableAtSelection = (0, _react.useCallback)(() => {
    editor.update(() => {
      const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
      tableNode.remove();
      clearTableSelection();
      onClose();
    });
  }, [editor, cell_node, clearTableSelection, onClose]);
  const deleteTableColumnAtSelection = (0, _react.useCallback)(() => {
    editor.update(() => {
      const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
      const tableColumnIndex = (0, _table.$getTableColumnIndexFromTableCellNode)(cell_node);
      (0, _table.$deleteTableColumn)(tableNode, tableColumnIndex);
      clearTableSelection();
      onClose();
    });
  }, [editor, cell_node, clearTableSelection, onClose]);
  const toggleTableRowIsHeader = (0, _react.useCallback)(isHeader => {
    editor.update(() => {
      const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
      const tableRowIndex = (0, _table.$getTableRowIndexFromTableCellNode)(cell_node);
      const tableRows = tableNode.getChildren();
      if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.');
      }
      const tableRow = tableRows[tableRowIndex];
      if (!(0, _table.$isTableRowNode)(tableRow)) {
        throw new Error('Expected table row');
      }
      tableRow.getChildren().forEach(tableCell => {
        if (!(0, _table.$isTableCellNode)(tableCell)) {
          throw new Error('Expected table cell');
        }
        tableCell.toggleHeaderStyle(_table.TableCellHeaderStates.ROW);
      });
      clearTableSelection();
      onClose();
    });
  }, [editor, cell_node, clearTableSelection, onClose]);
  const toggleTableColumnIsHeader = (0, _react.useCallback)(isHeader => {
    editor.update(() => {
      const tableNode = (0, _table.$getTableNodeFromLexicalNodeOrThrow)(cell_node);
      const tableColumnIndex = (0, _table.$getTableColumnIndexFromTableCellNode)(cell_node);
      const tableRows = tableNode.getChildren();
      for (let r = 0; r < tableRows.length; r++) {
        const tableRow = tableRows[r];
        if (!(0, _table.$isTableRowNode)(tableRow)) {
          throw new Error('Expected table row');
        }
        const tableCells = tableRow.getChildren();
        if (tableColumnIndex >= tableCells.length || tableColumnIndex < 0) {
          throw new Error('Expected table cell to be ' + 'inside of table row.');
        }
        const tableCell = tableCells[tableColumnIndex];
        if (!(0, _table.$isTableCellNode)(tableCell)) {
          throw new Error('Expected table cell');
        }
        tableCell.toggleHeaderStyle(_table.TableCellHeaderStates.COLUMN);
      }
      clearTableSelection();
      onClose();
    });
  }, [editor, cell_node, clearTableSelection, onClose]);
  const output = /*#__PURE__*/(0, _reactDom.createPortal)( /*#__PURE__*/_react.default.createElement("div", {
    className: "dropdown",
    ref: dropDownRef,
    onClick: e => e.stopPropagation()
  }, /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => insertTableRowAtSelection(false)
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Ins\xE9rer", ' ', selectionCounts.rows === 1 ? 'une ligne' : selectionCounts.rows + ' lignes', ' ', "au dessus")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => insertTableRowAtSelection(true)
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Ins\xE9rer", ' ', selectionCounts.rows === 1 ? 'une ligne' : selectionCounts.rows + ' lignes', ' ', "en dessous")), /*#__PURE__*/_react.default.createElement("hr", null), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => insertTableColumnAtSelection(false)
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Ins\xE9rer", ' ', selectionCounts.columns === 1 ? 'une colonne' : selectionCounts.columns + ' colonnes', ' ', "\xE0 gauche")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => insertTableColumnAtSelection(true)
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Ins\xE9rer", ' ', selectionCounts.columns === 1 ? 'une colonne' : selectionCounts.columns + ' colonnes', ' ', "\xE0 droite")), /*#__PURE__*/_react.default.createElement("hr", null), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => deleteTableColumnAtSelection()
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Supprimer la colonne")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => deleteTableRowAtSelection()
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Supprimer la ligne")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => deleteTableAtSelection()
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Supprimer tout le tableau")), /*#__PURE__*/_react.default.createElement("hr", null), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => toggleTableRowIsHeader()
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, (cell_node.__headerState & _table.TableCellHeaderStates.ROW) === _table.TableCellHeaderStates.ROW ? 'Enlever le surlignage de ligne' : 'Surligner la ligne ')), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => toggleTableColumnIsHeader()
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, (cell_node.__headerState & _table.TableCellHeaderStates.COLUMN) === _table.TableCellHeaderStates.COLUMN ? 'Enlever le surlignage de colonne' : 'Surligner la colonne '))), document.body);
  return output;
}
function TableCellActionMenuContainer() {
  const [editor] = (0, _LexicalComposerContext.useLexicalComposerContext)();
  const menuButtonRef = (0, _react.useRef)(null);
  const menuRootRef = (0, _react.useRef)(null);
  const [isMenuOpen, setIsMenuOpen] = (0, _react.useState)(false);
  const [tableCellNode, setTableMenuCellNode] = (0, _react.useState)(null);
  const moveMenu = (0, _react.useCallback)(() => {
    const menu = menuButtonRef.current;
    const selection = (0, _lexical.$getSelection)();
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;
    if (selection == null || menu == null) {
      setTableMenuCellNode(null);
      return;
    }
    const rootElement = editor.getRootElement();
    if ((0, _lexical.$isRangeSelection)(selection) && rootElement !== null && rootElement.contains(nativeSelection.anchorNode)) {
      const tableCellNodeFromSelection = (0, _table.$getTableCellNodeFromLexicalNode)(selection.anchor.getNode());
      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null);
        return;
      }
      const tableCellParentNodeDOM = editor.getElementByKey(tableCellNodeFromSelection.getKey());
      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null);
        return;
      }
      setTableMenuCellNode(tableCellNodeFromSelection);
    } else if (!activeElement) {
      setTableMenuCellNode(null);
    }
  }, [editor]);
  (0, _react.useEffect)(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        moveMenu();
      });
    });
  });
  (0, _react.useEffect)(() => {
    const menuButtonDOM = menuButtonRef.current;
    if (menuButtonDOM != null && tableCellNode != null) {
      const tableCellNodeDOM = editor.getElementByKey(tableCellNode.getKey());
      if (tableCellNodeDOM != null) {
        const tableCellRect = tableCellNodeDOM.getBoundingClientRect();
        const menuRect = menuButtonDOM.getBoundingClientRect();
        menuButtonDOM.style.opacity = '1';
        menuButtonDOM.style.left = tableCellRect.left + window.pageXOffset - menuRect.width + tableCellRect.width - 8 + 'px';
        menuButtonDOM.style.top = tableCellRect.top + window.pageYOffset + 10 + 'px';
      } else {
        menuButtonDOM.style.opacity = '0';
      }
    }
  }, [menuButtonRef, tableCellNode, editor]);
  const prevTableCellDOM = (0, _react.useRef)(tableCellNode);
  (0, _react.useEffect)(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false);
    }
    prevTableCellDOM.current = tableCellNode;
  }, [prevTableCellDOM, tableCellNode]);
  const output = /*#__PURE__*/_react.default.createElement("div", {
    className: "table-cell-action-button-container",
    ref: menuButtonRef
  }, tableCellNode != null ? /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "table-cell-action-button chevron-down",
    onClick: e => {
      e.stopPropagation();
      setIsMenuOpen(!isMenuOpen);
    },
    ref: menuRootRef
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "chevron-down"
  })), isMenuOpen ? /*#__PURE__*/_react.default.createElement(TableActionMenu, {
    contextRef: menuRootRef,
    setIsMenuOpen: setIsMenuOpen,
    onClose: () => setIsMenuOpen(false),
    tableCellNode: tableCellNode
  }) : undefined) : undefined);
  return output;
}
function TableActionMenuPlugin() {
  const [editor] = (0, _LexicalComposerContext.useLexicalComposerContext)();
  return (0, _react.useMemo)(() => /*#__PURE__*/(0, _reactDom.createPortal)( /*#__PURE__*/_react.default.createElement(TableCellActionMenuContainer, {
    editor: editor
  }), document.body), [editor]);
}
